import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { FaExclamationCircle, FaExclamationTriangle } from 'react-icons/fa';

export enum ComboBoxMode {
    Standard = 1,
    SearchableOneSelect,
    SearchableMultipleSelect,
}

export interface Option {
    value: string;
    label: string;
    description?: string;
    color?: string; // Description rengi için color alanı eklendi
}

interface ComboBoxProps {
    label: string;
    inputId: string;
    required?: boolean;
    errorMessage?: string;
    options: Option[];
    value: string | string[];
    onChange: (value: string | string[]) => void;
    mode?: ComboBoxMode;
    isLongTextToNextLine?: boolean;
    disabled?: boolean;
    searchable?: boolean;
    style?: React.CSSProperties;
    optionRenderer?: (option: Option) => JSX.Element;
    descriptionRenderer?: (description: string) => JSX.Element;
    className?: string;
}

interface ComboBoxHandle {
    validate: () => boolean;
    showError: () => void;
    hideError: () => void;
    getValue: () => string | string[];
    setValue: (value: string | string[]) => void;
}

const ComboBox = forwardRef<ComboBoxHandle, ComboBoxProps>(({
    label,
    inputId,
    required = false,
    errorMessage,
    options = [],
    value = '',
    onChange,
    mode = ComboBoxMode.Standard,
    isLongTextToNextLine = false,
    disabled = false,
    searchable = true,
    style,
    optionRenderer,
    descriptionRenderer,
    className = '',
}, ref) => {
    const [isValid, setIsValid] = useState(true);
    const [inputValue, setInputValue] = useState<string | string[]>(value || '');
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const comboBoxRef = useRef<HTMLDivElement | null>(null);

    const selectedOption = options.find(option => option.value === inputValue);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Dropdown'u kapatmak için dış tıklamaları izleme
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (comboBoxRef.current && !comboBoxRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useImperativeHandle(ref, () => ({
        validate: () => {
            if (required && ((typeof inputValue === 'string' && (inputValue === "0" || !inputValue)) || (Array.isArray(inputValue) && inputValue.length === 0))) {
                setIsValid(false);
                return false;
            }
            setIsValid(true);
            return true;
        },
        showError: () => {
            setIsValid(false);
        },
        hideError: () => {
            setIsValid(true);
        },
        getValue: () => inputValue,
        setValue: (value: string | string[]) => {
            setInputValue(value || '');
        },
    }));

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleOptionClick = (selectedValue: string) => {
        if (disabled) return;
        if (mode === ComboBoxMode.SearchableMultipleSelect) {
            const newValue = Array.isArray(inputValue)
                ? inputValue.includes(selectedValue)
                    ? inputValue.filter(v => v !== selectedValue)
                    : [...inputValue, selectedValue]
                : [selectedValue];

            setInputValue(newValue);
            onChange(newValue);
        } else {
            setInputValue(selectedValue);
            onChange(selectedValue);
            setDropdownOpen(false);
        }
    };

    const handleDropdownToggle = (event: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        event.stopPropagation();
        setDropdownOpen((prevOpen) => !prevOpen);
        setSearchTerm(''); // Arama terimini sıfırla
        setHighlightedIndex(-1);
    };

    const getDisplayLabel = (value: string | string[]) => {
        if (Array.isArray(value)) {
            return value.map(val => options.find(option => option.value === val)?.label || val).join(', ');
        }
        return options.find(option => option.value === value)?.label || 'Select...';
    };

    return (
        <div
            className={`form-group ${disabled ? 'disabled' : ''} ${className}`}
            style={{ position: 'relative', ...style }}
            ref={comboBoxRef}
            tabIndex={0}
        >
            <label className="form-label" htmlFor={inputId}>
                {label}
            </label>
            <div
                className={`form-control mb-0 ${!isValid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    overflow: 'hidden',
                    textOverflow: isLongTextToNextLine ? 'unset' : 'ellipsis',
                    whiteSpace: isLongTextToNextLine ? 'normal' : 'nowrap',
                }}
                onClick={handleDropdownToggle}
                data-value={inputValue}
            >
                {getDisplayLabel(inputValue)}
            </div>
            {dropdownOpen && !disabled && (
                <div
                    className="dropdown-menu show"
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        width: '100%',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                    }}
                >
                    {searchable && (
                        <input
                            type="text"
                            placeholder="Search..."
                            className="form-control"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                marginBottom: '8px',
                                width: 'calc(100% - 16px)',
                                margin: '8px',
                                padding: '4px 8px',
                            }}
                        />
                    )}
                    {filteredOptions.map((option, index) => (
                        <div
                            key={index}
                            className={`dropdown-item ${highlightedIndex === index ? 'highlighted' : ''}`}
                            onClick={() => handleOptionClick(option.value)}
                            style={{
                                cursor: 'pointer',
                                overflow: isLongTextToNextLine ? 'unset' : 'hidden',
                                textOverflow: isLongTextToNextLine ? 'unset' : 'ellipsis',
                                whiteSpace: isLongTextToNextLine ? 'normal' : 'nowrap',
                                backgroundColor: highlightedIndex === index ? '#007bff' : 'transparent',
                                color: highlightedIndex === index ? '#fff' : 'inherit'
                            }}
                        >
                            {optionRenderer ? optionRenderer(option) : option.label}
                        </div>
                    ))}
                </div>
            )}


            {!isValid && (
                <div className={`bg-soft-danger danger-container ${!selectedOption?.description ? 'danger-container-border' : ''}`}>
                    <div className="danger-icon">
                        <FaExclamationTriangle />
                    </div>
                    <div className="danger-text">
                        <span>
                            {errorMessage}
                        </span>
                    </div>
                </div>
            )}

            {selectedOption?.description && (
                <div className="warning-container bg-soft-warning">
                    <div className="warning-icon">
                        <FaExclamationCircle />
                    </div>
                    <div className="warning-text">
                        <span>
                            {descriptionRenderer
                                ? descriptionRenderer(selectedOption.description)
                                : selectedOption.description}
                        </span>
                    </div>
                </div>
            )}

        </div>
    );
});

export default ComboBox;
